import React from 'react';

const CHANGELOG = [
  {
    date: '8 Aug 2026',
    items: [
      'The tournament is over, so the app is now in archive mode: your predictions and the final leaderboard stay available to view, but the app is read-only and no longer sends reminders or fetches results. A banner at the top marks this. Everything is preserved and can be switched back on if needed.',
    ],
  },
  {
    date: '30 Jun 2026',
    items: [
      'Fixed: your default score now also counts in the knockout stage. Previously it only filled unpredicted group-stage matches, so a default that matched a knockout result (e.g. 2–1 for a 2–1 game) wasn’t scoring. Knockout matches you haven’t predicted now use your default, same as the group stage.',
    ],
  },
  {
    date: '28 Jun 2026',
    items: [
      'Knockout view on mobile: pick a round (Round of 32 → Final) and see that round’s ties as a mini-bracket — each pair of cards connected to the next-round tie — so it keeps the bracket feel on a phone. Tablets and desktop keep the full two-sided tree. Tap any tie to predict it.',
      'Corrected several knockout kick-off times and fixed two Round-of-16 pairings (the winners of Portugal v Croatia and Spain v Austria meet, likewise the other affected tie). Predictions now lock at the right moment for those matches.',
      'Knockout results now update automatically: your points are scored on the 90-minute result, while the actual outcome (extra time / penalties / who advanced) is captured for display. Anything decided beyond 90 minutes is flagged for an admin to confirm before it counts.',
      'Admin: new “Knockout results — review & override” panel listing every kicked-off knockout tie with its status (done / needs result / needs review) and a one-tap jump to enter or correct the result.',
      'Knockout bracket now advances itself: as each tie is decided, the winner is slotted into the next round automatically (so the Round of 16, quarters, semis and final fill in without manual entry).',
      'Admin: knockout matches in the result-entry, proxy-prediction and coverage selectors now show the real teams (e.g. “South Africa vs Canada”) instead of placeholders, updating automatically as the bracket fills in.',
    ],
  },
  {
    date: '26 Jun 2026',
    items: [
      'Knockout view is now a full tournament bracket — the classic two-sided tree with the Final in the middle. Tap any tie to set your prediction; scroll sideways to see the whole bracket on mobile.',
      'Knockout matches now also appear in the Schedule’s Matchday view, labelled by round (Round of 32, Last 16, Quarter, Semi, Final) rather than as numbered matchdays.',
      'Leaderboard: new stat columns — Pred (your real predictions, excluding default), Acc (correct results), Miss (wrong results), Def (matches scored from your default). The “JL” column is now labelled “Jokers Remaining”. On phones the table keeps position, name and points front-and-centre.',
      'End-of-tournament 8-bit celebration! When the Final result is in, the Knockout view crowns the winning team; once the tournament ends, the Leaderboard crowns the league champion — with a retro pixel trophy, confetti and an optional chiptune (tap to unmute). Tap 🏆 Replay to see it again.',
      'Knockout results now show the actual outcome — extra time and penalties — and highlight who advanced, while your points are still scored on the 90-minute result (shown alongside so you can compare your prediction).',
      'Match coverage (admin): players who didn’t predict but had a default score now show that default (e.g. “2-1 (d)”) and the points it scored, instead of appearing blank — they stay marked red as non-predictors.',
      'Admin proxy predictions: the match picker now defaults to the next upcoming game and groups matches into Upcoming (soonest first) and Played, with dates — no more scrolling past the whole group stage to find the relevant fixture.',
    ],
  },
  {
    date: '24 Jun 2026',
    items: [
      'Stay logged in: your session now persists when you close the tab or browser, so you no longer have to sign in every visit. You\'ll only be asked to log in again after 7 days.',
    ],
  },
  {
    date: '22 Jun 2026',
    items: [
      'Admin: re-inviting a placeholder whose first invite expired now resends the invitation email with a fresh password, instead of failing with "a user with this email already exists"',
      'Invite and password-reset emails now send via our own verified domain (SES) instead of the default Cognito mailer — better deliverability (fewer invites lost to spam filters, especially iCloud) and higher sending limits',
    ],
  },
  {
    date: '16 Jun 2026',
    items: [
      'Placeholder invites: when an invited placeholder logs in for the first time, their league, predictions, and email are now correctly migrated to the new account (previously the merge was silently failing)',
      'Admin: new "Rebuild snapshots" button on the Leaderboard card — recomputes position-history snapshots without needing a new result (global admin only)',
      'Leaderboard table (mobile): player name no longer hidden — Winner Pick column drops first when space is tight',
      'Position history charts: tidier on mobile (smaller axes, no rotated labels), and now start with the last 10 matches so the line is readable; "Show all" toggles full tournament view',
      'Leaderboard: three new stat columns — GP (games played), 2× (exact-score predictions), JL (jokers left out of 4). Hover or long-press the headers for the full names. Hidden on portrait phones to keep the player name and points visible.',
      'League history chart: new Position / Points toggle — Points mode plots cumulative points over matches, starting from zero',
      'League history chart: click any player name in the legend to hide/show their line — handy for comparing two or three players in a busy chart',
      'Polish + older-device pass: iOS Safari address-bar jump fixed (dynamic viewport), buttons now meet 44 px tap-target minimum, section headers across the app share one consistent style, and a tighter layout kicks in on narrow phones (≤ 380 px) so older iPhones and small Androids fit better.',
      'Tablet layout (768–1023 px, iPad portrait, large Androids, landscape phones): match modal opens wider for more chart room, leaderboard table cells tighten so all stat columns fit comfortably, group standings show full team names.',
      'Leaderboard: league selector wraps to a second line on narrow phones instead of side-scrolling. Matches view selector also wraps on narrow phones rather than side-scrolling out of view.',
      'Matches: the matchday selector now snaps the current matchday to the left of the strip on load, with upcoming matchdays visible to the right (was: scrolled to MD1 with the active matchday off-screen).',
      'Matches: Friendlies tab hidden now the real tournament is on — admins can still configure and use them, just no public tab. Will return when the next round of pre-tournament tests rolls around.',
      'History charts: new "Show deltas" toggle. In Points mode each match shows "+N" earned; in Position mode it shows ↑N / ↓N. Off by default so the chart stays clean — flick it on when you want the per-match story. In the league overlay deltas only show on your line so the other lines stay readable.',
    ],
  },
  {
    date: '15 Jun 2026',
    items: [
      'Match modal: goal scorers and bookings now shown in a timeline (home team left, away team right) — pulled live from ESPN with openfootball fallback',
      'Match modal: attendance and referee shown at full time when available',
    ],
  },
  {
    date: '14 Jun 2026',
    items: [
      'Leaderboard: ↑/↓ movement indicator next to each rank, showing how positions changed since the last match result',
      'Leaderboard: tap any player\'s row to see their rank trajectory through the tournament',
      'Leaderboard: new History button (top right) opens a chart with every player\'s position over time — your line is highlighted',
      'Match modal: now shows the stadium and host city under the meta row',
    ],
  },
  {
    date: '13 Jun 2026',
    items: [
      'Live banner on Dashboard and Matches — shows the score and minute when a game is in progress; tap to open the match',
      'Matches now defaults to Matchday view, auto-selecting the current matchday',
      'Matchday view: completed matches are greyed out but still clickable to see results and points',
      'Hide completed button now only appears in Calendar view where it is most useful',
      'Leaderboard: first league now auto-selected on load — no more full-page picker when in multiple leagues',
      'Match modal: live score now updates automatically while the modal is open (no need to close and reopen)',
      'Matchday view: scrollbar spacing improved so it no longer overlaps the match list',
    ],
  },
  {
    date: '12 Jun 2026',
    items: [
      'Default prediction now shows prominently below live score in match modal',
      'Leaderboard: default score no longer awards points for matches that finished before the user registered',
      'Live match cards: score moves to footer as "Pred · X–Y" when kick-off passes; live score shows in centre; "↻ 1m" indicator on live matches',
      'Match modal: live score correctly shows your pick (or default) below it; "↻ Updates every 1 min" shown; default prediction no longer falls back to 1–1',
      'Live scores now update every 1 minute (previously every 2 minutes)',
      'Email reminders: opt in to receive a reminder ~1 hour before each matchday when you have unpredicted matches',
      'Privacy Notice added — read it at /privacy',
      'First-time consent gate records your acceptance of the privacy terms',
      'Account: delete your account and all data at any time (right to erasure)',
      'Schedule view preference (Calendar or Matchday) is now remembered between sessions',
      'Show/hide completed preference is now remembered between sessions',
      'Calendar view: hide completed now keeps yesterday\'s finished matches visible so you can check recent results',
      'Matchday view: hide completed now keeps the most recently completed matchday visible alongside upcoming ones',
      'Admin: configurable email reminder window (minutes before KO) — change without redeploying',
      'Account: Privacy Notice link added at the bottom of the page',
      'Account: enable email reminders now shows a proper consent modal before opting in',
      'Email reminders: confirmation email sent when reminders are enabled',
      'Admin: invite user now refreshes the player list immediately after sending',
      'Admin: global admin can no longer delete their own account',
      'Points badge no longer shows ×2 twice for joker picks',
      'Dashboard reorganised: Tournament Winner and Default Score moved above How to Play; What\'s New moved to bottom',
      'League/Global chips removed from Dashboard and Matches screens',
      'Live match modal: injury time displays correctly (e.g. 45+2\') — double-apostrophe bug fixed',
      'Email reminders: now sent ~2 hours before each matchday (previously ~1 hour); reminder window widened to 30 minutes',
      'Email reminders: email and Account page now show the configured lead time dynamically rather than a hardcoded "1 hour"',
      'Email reminders: dedup window extended to 14 hours to guarantee one reminder per matchday even on the longest matchdays',
      'Live scores: stale minute hidden and "Waiting for update…" shown when ESPN data is more than 4 minutes old',
      'Match modal: after kick-off, see predictions from everyone in your leagues with a Show/Hide scores toggle',
      'Match modal: at full time, league players now see coloured points badges (+2/+1/+0) next to each prediction, matching the admin view',
      'Leaderboard: tied players now share a position (1,1,3,3,3,3,5… instead of sequential numbering)',
      'Leaderboard: joker ×2 indicator now always shown even when the prediction scores zero points',
    ],
  },
  {
    date: '12 Jun 2026',
    items: [
      'Live scores update in real time during matches',
      'Match results recorded automatically at full time',
      'Post-match screen now shows points earned per prediction (+2 exact, +1 correct result, +0 wrong) with ×2 for joker picks',
      'Correcting a match result now pre-fills the score form with the existing value',
      'New Matchday view in Schedule tab — switch between Calendar and Matchday to browse matches by MD1–MD17',
      'Matchday chip navigation auto-selects the current matchday on load',
      'Global admin can override the matchday assignment for any group-stage match from the Admin page',
    ],
  },
  {
    date: '11 Jun 2026',
    items: [
      'League admins can now invite players directly to their league',
      'League admin page opens your league automatically — no chip click required',
      'Proxy predictions: set a tournament winner pick for any player',
      'Match dropdowns now sort in numerical order (M1, M2, M3… not M1, M10, M2…)',
      'League admins can delete players who are exclusively in their league',
      'Password reset now works for players who have never logged in (re-sends welcome email)',
    ],
  },
  {
    date: '10 Jun 2026',
    items: [
      'Match cards now show the actual result in the centre when a game is finished',
      'Your prediction moves to the footer with points earned',
      'Team names no longer overflow on narrow mobile screens',
      'Separate Changelog and Audit Log pages',
      'Admin page reorganised: league-admin tools at the top',
    ],
  },
  {
    date: '9 Jun 2026',
    items: [
      'Placeholder players — create offline players, enter predictions for them, invite later',
      'Proxy prediction entry — admin can set or update any player\'s predictions',
      'Share predictions to WhatsApp from any match',
      "See everyone's predictions after kick-off",
      'Joker warning banner if group jokers unused',
      'Match rows now show your predicted score',
      'Countdown to kick-off on upcoming matches',
      'Mobile layout improvements across all pages',
    ],
  },
  {
    date: '8 Jun 2026',
    items: [
      'Multi-league support (invite-based leagues)',
      'Winner pick window: change after group stage (5 pts), locked during group stage',
      'Default score now applies to all unset matches',
      'Export all predictions fixed',
      'Leaderboard shows max 226 pts correctly',
    ],
  },
];

export default function Changelog() {
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="kicker" style={{ marginBottom: 6 }}>WC2026 Predictor</div>
          <h1 className="page-title">What's New</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>Recent changes to the app</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {CHANGELOG.map(entry => (
          <div key={entry.date} className="card">
            <div className="section-kicker" style={{ marginBottom: '0.75rem' }}>
              {entry.date}
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--text-muted)', fontSize: 'var(--t-sm)', lineHeight: 1.8 }}>
              {entry.items.map(item => <li key={item}>{item}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
